import {
  type BlockAlign,
  type BlockSpacing,
  type BlockVisual,
  type BlockWidth,
  DEFAULT_EXPERIENCE_THEME,
  type ExperienceTheme,
  type ResolvedExperienceTheme,
  type TextColorToken,
} from '../schemas/experience.js';

/**
 * The THREE LEVELS of override of §9.4, merged, PURE and shared.
 *
 * « Trois niveaux de surcharge, tous partiels et fusionnés dans l'ordre : le
 * thème de l'expérience, puis la page, puis le bloc. Un token absent hérite du
 * niveau au-dessus. » This is that merge, and it lives in `shared` for the same
 * reason `renderRichText` does: the editor's preview and the reader must paint
 * the same page, and a merge written twice is two paintings of one theme.
 *
 * The PMS default sits under all three (`DEFAULT_EXPERIENCE_THEME`), so the
 * result is TOTAL: every token has a value, and no caller ever branches on an
 * absent one. That totality is what lets the contrast check of §9.4 name a
 * pair - it always has two colours to compare.
 */

/**
 * A theme with every token resolved, plus the four a BLOCK settles: its
 * alignment, its width, its spacing (§9.2) and the colour of its text (§9.4).
 *
 * The four are NOT theme tokens - the bar has no control for them at the
 * experience level - so they carry their own defaults here rather than in
 * `DEFAULT_EXPERIENCE_THEME`, which would have offered an experience-wide
 * alignment that §9.4 does not have.
 */
export type ResolvedVisual = ResolvedExperienceTheme & {
  align: BlockAlign;
  width: BlockWidth;
  spacing: BlockSpacing;
  /** Which of the theme's three text colours this block's words take (H28). */
  textColor: TextColorToken;
};

/** What a block that overrides nothing looks like. */
const BLOCK_DEFAULTS = {
  align: 'left',
  width: 'full',
  spacing: 'normal',
  textColor: 'text',
} as const satisfies Pick<ResolvedVisual, 'align' | 'width' | 'spacing' | 'textColor'>;

/**
 * Drops the keys whose value is `undefined` before a spread.
 *
 * A partial theme parsed by Zod carries only the keys it holds, but a theme
 * built in code - a test, the editor's state - can carry `{ accent: undefined }`,
 * and a plain spread would then write `undefined` OVER the level below. That is
 * the one way a merge of partials silently loses a token, and it is why this
 * function exists rather than three spreads in a row.
 */
function defined(theme: ExperienceTheme | undefined): Partial<ResolvedExperienceTheme> {
  if (theme === undefined) return {};
  const kept: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(theme)) {
    if (value !== undefined) kept[key] = value;
  }
  return kept as Partial<ResolvedExperienceTheme>;
}

/**
 * The visual in force for one block, on one page, of one experience (§9.4).
 *
 * A block overrides three theme tokens at most - alignment, size and text
 * colour - so `blockVisualSchema` carries `textScale` and nothing else of the
 * theme: a block that set a background colour would be free CSS by another
 * name.
 *
 * Called with no page and no block, it answers the experience's own theme,
 * which is what the bar shows when nothing is selected and what the reader
 * paints the page frame with.
 */
export function resolveTheme(
  theme: ExperienceTheme | undefined,
  page?: ExperienceTheme | undefined,
  block?: BlockVisual | undefined,
): ResolvedVisual {
  const merged: ResolvedExperienceTheme = {
    ...DEFAULT_EXPERIENCE_THEME,
    ...defined(theme),
    ...defined(page),
  };

  return {
    ...merged,
    textScale: block?.textScale ?? merged.textScale,
    align: block?.align ?? BLOCK_DEFAULTS.align,
    width: block?.width ?? BLOCK_DEFAULTS.width,
    spacing: block?.spacing ?? BLOCK_DEFAULTS.spacing,
    textColor: block?.textColor ?? BLOCK_DEFAULTS.textColor,
  };
}

/**
 * Which of the two page faces a theme names, so the reader loads ONLY those
 * (§9.4: « n'est chargée que si un thème la nomme »). Deduplicated, in a stable
 * order - the title face first, because it is the one painted above the fold.
 */
export function fontsInUse(visual: ResolvedVisual | ResolvedExperienceTheme): string[] {
  return [...new Set([visual.titleFont, visual.textFont])];
}
